import React, { useState, useEffect, useRef } from "react";
import { FiEye, FiEyeOff } from "react-icons/fi";
import "./ExpertSignup.css";
import { auth, db } from "../firebase"; 
import { createUserWithEmailAndPassword, deleteUser } from "firebase/auth";
import { doc, setDoc, serverTimestamp, addDoc, collection, getDocs, getDoc, query, where } from "firebase/firestore";
import logoImg from "./Sorted_Logo.png";

// Import your clean external legal strings
import { privacyPolicyText, termsOfServiceText } from "./LegalTexts";

// Same backend the dashboard already uses for bank docs / profile photos.
const API_BASE_URL = process.env.REACT_APP_API_URL || "http://localhost:3001";

// Uploads a file via the backend's presigned-URL flow instead of talking to S3
// directly from the browser. This used to instantiate an AWS.S3 client here
// with REACT_APP_AWS_ACCESS_KEY_ID / REACT_APP_AWS_SECRET_ACCESS_KEY — but any
// env var prefixed REACT_APP_ gets baked verbatim into the built JS bundle by
// Create React App, which means the real AWS secret key was shipped in plain
// text to every visitor's browser. The backend already has a safe
// /api/experts/generate-presigned-url endpoint (used by ExpertDashboard.js for
// bank docs and profile photos) that does this server-side instead.
const uploadViaPresignedUrl = async (file, folderPath, idToken) => {
  if (!file) throw new Error("No file provided for upload.");

  const presignRes = await fetch(`${API_BASE_URL}/api/experts/generate-presigned-url`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${idToken}`,
    },
    body: JSON.stringify({
      fileName: file.name,
      fileType: file.type,
      folderPath,
    }),
  });
  const presignData = await presignRes.json();
  if (!presignRes.ok || !presignData.success) {
    throw new Error(presignData.error || "S3 upload failed: could not get an upload authorization from the server.");
  }

  const uploadRes = await fetch(presignData.uploadUrl, {
    method: "PUT",
    headers: { "Content-Type": file.type || "application/octet-stream" },
    body: file,
  });
  if (!uploadRes.ok) {
    throw new Error("S3 upload failed: file upload to storage failed.");
  }

  return presignData.publicUrl;
};

function ExpertSignup({ onLoginClick, setIsRegistering }) {
  // Correctly placed state trackers inside the active functional component body
  const [modalContent, setModalContent] = useState({ isOpen: false, title: "", text: "" });
  const [currentStep, setCurrentStep] = useState(1);
  const [errorBanner, setErrorBanner] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [uploadStatus, setUploadStatus] = useState("");

  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const [companyName, setCompanyName] = useState("");
  const [industries, setIndustries] = useState([]);
  const [othersInputOpen, setOthersInputOpen] = useState(false);
  const [othersText, setOthersText] = useState("");
  const [experienceYearRow, setExperienceYearRow] = useState("");
  const [bio, setBio] = useState("");

  const MAX_INDUSTRY_SELECTIONS = 2;
  const MAX_BIO_WORDS = 200;
  const MAX_BIO_CHARS = 1500;

  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  const bioWordCount = bio.trim() === "" ? 0 : bio.trim().split(/\s+/).filter(Boolean).length;

  const toggleIndustrySelection = (ind) => {
    // "Others" opens a custom text input instead of being added literally
    if (ind === "Others") {
      setOthersInputOpen(true);
      setOthersText("");
      return;
    }
    setIndustries(prev => {
      if (prev.includes(ind)) {
        return prev.filter(i => i !== ind);
      }
      if (prev.length >= MAX_INDUSTRY_SELECTIONS) {
        setErrorBanner(`⚠️ You can select up to ${MAX_INDUSTRY_SELECTIONS} industries only.`);
        return prev;
      }
      setErrorBanner("");
      return [...prev, ind];
    });
  };

  const handleOthersSubmit = () => {
    const val = othersText.trim();
    if (!val) { setOthersInputOpen(false); return; }
    setIndustries(prev => {
      if (prev.includes(val)) { setOthersInputOpen(false); setOthersText(""); return prev; }
      if (prev.length >= MAX_INDUSTRY_SELECTIONS) {
        setErrorBanner(`⚠️ You can select up to ${MAX_INDUSTRY_SELECTIONS} industries only.`);
        setOthersInputOpen(false); setOthersText(""); return prev;
      }
      setErrorBanner("");
      return [...prev, val];
    });
    setOthersInputOpen(false);
    setOthersText("");
  };

  const handleBioChange = (e) => {
    let value = e.target.value;
    if (value.length > MAX_BIO_CHARS) {
      value = value.slice(0, MAX_BIO_CHARS);
    }
    const words = value.trim() === "" ? [] : value.trim().split(/\s+/).filter(Boolean);
    if (words.length > MAX_BIO_WORDS) {
      value = words.slice(0, MAX_BIO_WORDS).join(" ");
    }
    setBio(value);
  };

  const [location, setLocation] = useState("");
  const [businessLogo, setBusinessLogo] = useState(null);
  const [profilePhoto, setProfilePhoto] = useState(null);
  
  const logoInputRef = useRef(null);
  const photoInputRef = useRef(null);
  const [agreedToTerms, setAgreedToTerms] = useState(false);

  useEffect(() => {
    setEmail("");
    setPassword("");
    setConfirmPassword("");
  }, []);

  // ═══════════════════════════════════════════════════════════════════
  // BROWSER / DEVICE BACK-BUTTON SUPPORT
  // Stepping through this wizard is all React state (currentStep), so the
  // back button would otherwise have nothing to undo and just leave the
  // page. Wire each step change into the History API: pressing back steps
  // you back one screen at a time instead of exiting the signup flow, and
  // if the Terms/Privacy modal is open, back closes just that modal first.
  // ═══════════════════════════════════════════════════════════════════
  const isPopNavRef = useRef(false);
  const lastPushedStepRef = useRef(1);
  const modalWasOpenRef = useRef(false);

  useEffect(() => {
    if (!window.history.state || !window.history.state.expertSignup) {
      window.history.replaceState({ expertSignup: true, step: 1, modalOpen: false }, "");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (isPopNavRef.current) return;
    if (currentStep !== lastPushedStepRef.current) {
      window.history.pushState({ expertSignup: true, step: currentStep, modalOpen: false }, "");
      lastPushedStepRef.current = currentStep;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentStep]);

  useEffect(() => {
    if (isPopNavRef.current) { modalWasOpenRef.current = modalContent.isOpen; return; }
    if (modalContent.isOpen && !modalWasOpenRef.current) {
      window.history.pushState({ expertSignup: true, step: currentStep, modalOpen: true }, "");
    }
    modalWasOpenRef.current = modalContent.isOpen;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modalContent.isOpen]);

  useEffect(() => {
    const handlePopState = (e) => {
      const state = e.state;
      isPopNavRef.current = true;

      if (state && state.expertSignup) {
        lastPushedStepRef.current = state.step;
        setCurrentStep(state.step);
        setModalContent((prev) => (state.modalOpen ? prev : { isOpen: false, title: "", text: "" }));
      } else {
        // Nothing left to undo in the wizard — hand back to the login screen
        // rather than leaving the site outright, if that toggle is available.
        if (typeof setIsRegistering === "function") setIsRegistering(false);
      }

      setTimeout(() => { isPopNavRef.current = false; }, 0);
    };

    window.addEventListener("popstate", handlePopState);
    return () => window.removeEventListener("popstate", handlePopState);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const availableIndustriesList = [
    // Core business categories
    "Technology & SaaS",
    "Finance & Fintech",
    "Healthcare",
    "E-commerce & Retail",
    "Marketing & Growth",
    "Legal & Compliance",
    "HR & Talent",
    "Product Management",
    "Design & UX",
    "Operations & Supply Chain",
    "Education & EdTech",
    "Real Estate",
    // New categories
    "Food & Beverage / Restaurants",
    "Fashion & Clothing",
    "Beauty & Wellness",
    "Agriculture & Food Chain",
    "Manufacturing & Industrial",
    "Media & Entertainment",
    "Travel & Hospitality",
    "Sports & Fitness",
    "Sustainability & CleanTech",
    // Always last — triggers custom text input
    "Others",
  ];

  // validateStep is async for Step 1 — needs to check Firestore for duplicate emails.
  // All other steps are synchronous (local validation only).
  const [checkingEmail, setCheckingEmail] = useState(false);

  const validateStep = async () => {
    setErrorBanner("");
    if (currentStep === 1) {
      if (!firstName.trim() || !lastName.trim() || !email.trim() || !password || !confirmPassword) {
        setErrorBanner("⚠️ All validation fields are required.");
        return false;
      }
      if (!emailRegex.test(email.trim())) {
        setErrorBanner("⚠️ Please enter a valid email address.");
        return false;
      }
      if (password.length < 8) {
        setErrorBanner("⚠️ Password security layout requirement: Minimum 8 characters.");
        return false;
      }
      if (password !== confirmPassword) {
        setErrorBanner("⚠️ Password and Confirm Password do not match.");
        return false;
      }

      // ── Email existence check ──────────────────────────────────────────────
      // Check both experts and users collections before letting them proceed.
      // This catches the error at Step 1 instead of after filling 5 more steps.
      setCheckingEmail(true);
      try {
        const trimmedEmail = email.trim().toLowerCase();

        // Check experts collection — allow re-signup if all matching docs are deleted
        const expertQuery = await getDocs(
          query(collection(db, "experts"), where("email", "==", trimmedEmail))
        );
        if (!expertQuery.empty) {
          const activeExpert = expertQuery.docs.find(d => d.data()?.approvalStatus !== "deleted");
          if (activeExpert) {
            setErrorBanner("⚠️ This email is already registered as an expert. Please sign in instead.");
            setCheckingEmail(false);
            return false;
          }
          // All matching expert docs are deleted — re-signup allowed, carry forward data below
        }

        // Check users collection — block if active user account exists with this email
        const userQuery = await getDocs(
          query(collection(db, "users"), where("email", "==", trimmedEmail), where("status", "!=", "deleted"))
        );
        if (!userQuery.empty) {
          setErrorBanner("⚠️ This email is already registered as a user account on SORTED. Please use a different email or log in at thesorted.co.in.");
          setCheckingEmail(false);
          return false;
        }

        // Also check Firebase Auth via fetchSignInMethodsForEmail
        // (catches accounts created via Google or other providers)
        try {
          const { fetchSignInMethodsForEmail } = await import("firebase/auth");
          const { auth: fbAuth } = await import("../firebase");
          const methods = await fetchSignInMethodsForEmail(fbAuth, trimmedEmail);
          if (methods && methods.length > 0) {
            setErrorBanner("⚠️ An account with this email already exists. Please sign in or use a different email.");
            setCheckingEmail(false);
            return false;
          }
        } catch {
          // fetchSignInMethodsForEmail failure is non-fatal — skip this check
        }

      } catch (err) {
        console.error("Email check failed:", err);
        // Non-fatal — let them proceed; duplicate will be caught at final submit
      } finally {
        setCheckingEmail(false);
      }

    } else if (currentStep === 2) {
      if (!companyName.trim() || industries.length === 0 || !experienceYearRow || !bio.trim()) {
        setErrorBanner("⚠️ Please populate all details and select an experience profile.");
        return false;
      }
      if (industries.length > MAX_INDUSTRY_SELECTIONS) {
        setErrorBanner(`⚠️ You can select up to ${MAX_INDUSTRY_SELECTIONS} industries only.`);
        return false;
      }
      if (bioWordCount > MAX_BIO_WORDS) {
        setErrorBanner(`⚠️ Short bio must be ${MAX_BIO_WORDS} words or fewer.`);
        return false;
      }
      if (bio.length > MAX_BIO_CHARS) {
        setErrorBanner(`⚠️ Short bio must be ${MAX_BIO_CHARS} characters or fewer.`);
        return false;
      }
    } else if (currentStep === 3) {
      if (!location.trim()) {
        setErrorBanner("⚠️ City or place of business coordinates required.");
        return false;
      }
    } else if (currentStep === 4) {
      if (!profilePhoto) {
        setErrorBanner("⚠️ A professional snapshot headshot file attachment is mandatory.");
        return false;
      }
    }
    return true;
  };

  return (
    <div className="glass-signup-page">
      <div className="glass-signup-blur-overlay"></div>
      <div className="glass-signup-container">
        
        <div className="glass-signup-brand-header">
          <div className="glass-signup-logo-inner-row">
            <img src={logoImg} alt="SORTED Logo" className="glass-signup-logo" />
            <div className="glass-signup-text-stack">
              <span className="glass-brand-title">SORTED</span>
            </div>
          </div>
          <span className="glass-signup-tagline">Expert Onboarding Portal</span>
        </div>

        <div className="glass-stepper-row">
          {[
            { step: 1, title: "Account" },
            { step: 2, title: "Business" },
            { step: 3, title: "Location" },
            { step: 4, title: "Photo" },
            { step: 5, title: "Agreement" },
            { step: 6, title: "Complete" }
          ].map((item) => (
            <div key={item.step} className="glass-step-node">
              <div className={`glass-step-circle ${currentStep === item.step ? "active" : currentStep > item.step ? "completed" : ""}`}>
                {currentStep > item.step ? "✓" : item.step}
              </div>
              <span className={`glass-step-title-text ${currentStep === item.step ? "active" : ""}`}>{item.title}</span>
            </div>
          ))}
        </div>

        <div className="glass-signup-card">
          {errorBanner && <div className="glass-signup-error-banner-alert">{errorBanner}</div>}

          {currentStep === 1 && (
            <div className="glass-step-workspace glass-fade-animation">
              <h2>Create your expert account</h2>
              <p className="glass-step-subtitle">Join SORTED as a verified business expert</p>

              <input type="text" name="fake_user" style={{ display: "none" }} autoComplete="username" readOnly />
              <input type="password" name="fake_pass" style={{ display: "none" }} autoComplete="new-password" readOnly />

              <div className="glass-grid-two-columns">
                <div className="glass-input-field-block">
                  <label>First Name</label>
                  <input type="text" value={firstName} onChange={e => setFirstName(e.target.value)} placeholder="Rahul" autoComplete="off" className="glass-form-input-ctrl" />
                </div>
                <div className="glass-input-field-block">
                  <label>Last Name</label>
                  <input type="text" value={lastName} onChange={e => setLastName(e.target.value)} placeholder="Sharma" autoComplete="off" className="glass-form-input-ctrl" />
                </div>
              </div>

              <div className="glass-input-field-block glass-margin-top-gap">
                <label>Email Address</label>
                <input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="rahul@company.com" autoComplete="off" autoCorrect="off" autoCapitalize="off" spellCheck="false" className="glass-form-input-ctrl" />
              </div>

              <div className="glass-input-field-block glass-margin-top-gap">
                <label>Password</label>
                <div className="glass-password-field-wrapper">
                  <input
                    type={showPassword ? "text" : "password"}
                    value={password}
                    onChange={e => setPassword(e.target.value)}
                    placeholder="Min 8 characters"
                    autoComplete="new-password"
                    className="glass-form-input-ctrl glass-password-input-padded"
                  />
                  <button
                    type="button"
                    className="glass-password-toggle-btn"
                    onClick={() => setShowPassword(prev => !prev)}
                    tabIndex={-1}
                  >
                    {showPassword ? <FiEyeOff size={17} /> : <FiEye size={17} />}
                  </button>
                </div>
              </div>

              <div className="glass-input-field-block glass-margin-top-gap">
                <label>Confirm Password</label>
                <div className="glass-password-field-wrapper">
                  <input
                    type={showConfirmPassword ? "text" : "password"}
                    value={confirmPassword}
                    onChange={e => setConfirmPassword(e.target.value)}
                    placeholder="Re-enter your password"
                    autoComplete="new-password"
                    className="glass-form-input-ctrl glass-password-input-padded"
                  />
                  <button
                    type="button"
                    className="glass-password-toggle-btn"
                    onClick={() => setShowConfirmPassword(prev => !prev)}
                    tabIndex={-1}
                  >
                    {showConfirmPassword ? <FiEyeOff size={17} /> : <FiEye size={17} />}
                  </button>
                </div>
              </div>

              <button
                type="button"
                disabled={checkingEmail}
                onClick={async () => {
                  const valid = await validateStep();
                  if (valid) setCurrentStep(2);
                }}
                className="glass-action-continue-btn"
              >
                {checkingEmail ? "Checking…" : "Continue 〉"}
              </button>
              <p className="glass-login-fallback-footer">Already registered? <span onClick={onLoginClick}>Sign In</span></p>
            </div>
          )}

          {currentStep === 2 && (
            <div className="glass-step-workspace glass-fade-animation">
              <h2>Business Details</h2>
              <p className="glass-step-subtitle">Tell us about your expertise and company</p>

              <div className="glass-input-field-block">
                <label>Business / Company Name</label>
                <input type="text" value={companyName} onChange={e => setCompanyName(e.target.value)} placeholder="Sharma Ventures Pvt. Ltd." className="glass-form-input-ctrl" />
              </div>

              <div className="glass-input-field-block glass-margin-top-gap">
                <label>Industry / Expertise <span className="glass-field-hint">(select up to {MAX_INDUSTRY_SELECTIONS})</span></label>
                <select
                  value=""
                  onChange={(e) => { if (e.target.value) toggleIndustrySelection(e.target.value); }}
                  disabled={industries.length >= MAX_INDUSTRY_SELECTIONS}
                  className="glass-form-select-ctrl"
                >
                  <option value="">
                    {industries.length >= MAX_INDUSTRY_SELECTIONS ? "Maximum 2 selected" : "Select industry..."}
                  </option>
                  {availableIndustriesList
                    .filter((ind) => !industries.includes(ind))
                    .map((ind) => (
                      <option key={ind} value={ind}>{ind}</option>
                    ))}
                </select>

                {othersInputOpen && (
                  <div className="glass-others-input-row">
                    <input
                      type="text"
                      className="glass-form-input-ctrl"
                      placeholder="e.g. Jewellery & Gems, Photography, etc."
                      value={othersText}
                      onChange={e => setOthersText(e.target.value)}
                      onKeyDown={e => { if (e.key === "Enter") { e.preventDefault(); handleOthersSubmit(); } }}
                      autoFocus
                    />
                    <div className="glass-others-input-actions">
                      <button type="button" className="glass-action-continue-btn" style={{ padding: "8px 18px", fontSize: 13 }} onClick={handleOthersSubmit}>Add</button>
                      <button type="button" className="glass-action-back-btn" style={{ padding: "8px 14px", fontSize: 13 }} onClick={() => { setOthersInputOpen(false); setOthersText(""); }}>Cancel</button>
                    </div>
                  </div>
                )}

                {industries.length > 0 && (
                  <div className="glass-selected-industries-box">
                    {industries.map((ind) => (
                      <span key={ind} className="glass-industry-tag-pill">
                        {ind}
                        <button
                          type="button"
                          className="glass-industry-tag-remove-btn"
                          onClick={() => toggleIndustrySelection(ind)}
                          aria-label={`Remove ${ind}`}
                        >
                          ×
                        </button>
                      </span>
                    ))}
                  </div>
                )}
              </div>

              <div className="glass-input-field-block glass-margin-top-gap">
                <label>Years of Experience</label>
                <div className="glass-experience-selection-matrix-grid">
                  {[
                    { key: "Less than 6 months", label: "Less than 6 months" },
                    { key: "6 months – 1 year", label: "6 months – 1 year" },
                    { key: "1 – 3 years", label: "1 – 3 years" },
                    { key: "3 – 5 years", label: "3 – 5 years" },
                    { key: "5 – 10 years", label: "5 – 10 years" },
                    { key: "More than 10 years", label: "More than 10 years" }
                  ].map((cell) => (
                    <div 
                      key={cell.key} 
                      className={`glass-experience-option-card ${experienceYearRow === cell.key ? "selected" : ""}`}
                      onClick={() => setExperienceYearRow(cell.key)}
                    >
                      <span className="exp-label">{cell.label}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="glass-input-field-block glass-margin-top-gap">
                <label>Short Bio</label>
                <textarea value={bio} onChange={handleBioChange} rows={3} maxLength={MAX_BIO_CHARS} placeholder="Briefly describe your expertise..." className="glass-form-textarea-ctrl" />
                <span className={`glass-word-count-hint ${(bioWordCount >= MAX_BIO_WORDS || bio.length >= MAX_BIO_CHARS) ? "glass-word-count-limit" : ""}`}>
                  {bioWordCount}/{MAX_BIO_WORDS} words · {bio.length}/{MAX_BIO_CHARS} characters
                </span>
              </div>

              <div className="glass-dual-navigation-buttons-row">
                <button type="button" onClick={() => setCurrentStep(1)} className="glass-action-back-btn">〈 Back</button>
                <button type="button" onClick={() => { if (validateStep()) setCurrentStep(3); }} className="glass-action-continue-btn glass-adaptive-width">Continue 〉</button>
              </div>
            </div>
          )}

          {currentStep === 3 && (
            <div className="glass-step-workspace glass-fade-animation">
              <h2>Business Location</h2>
              <p className="glass-step-subtitle">Where is your business based?</p>

              <div className="glass-input-field-block">
                <label>City / Place of Business</label>
                <input type="text" value={location} onChange={e => setLocation(e.target.value)} placeholder="Mumbai, Maharashtra" className="glass-form-input-ctrl" />
              </div>

              <div className="glass-input-field-block glass-margin-top-gap">
                <label>Business Logo</label>
                <div 
                  className="glass-file-upload-drag-drop-zone"
                  onClick={() => logoInputRef.current && logoInputRef.current.click()}
                  style={{ position: 'relative' }}
                >
                  <input 
                    ref={logoInputRef}
                    type="file" 
                    accept="image/*" 
                    style={{ display: "none" }}
                    onChange={e => setBusinessLogo(e.target.files[0])} 
                  />
                  <div className="glass-drag-drop-inner-content-axis">
                    <span className="upload-cloud-icon">📤</span>
                    <p className="upload-main-msg">Click to upload logo</p>
                    <p className="upload-sub-msg">PNG, JPG or SVG — Max 5MB</p>
                  </div>
                  {businessLogo && <div className="glass-selected-file-pill-alert">✓ Logo Selected: {businessLogo.name}</div>}
                </div>
              </div>

              <div className="glass-dual-navigation-buttons-row">
                <button type="button" onClick={() => setCurrentStep(2)} className="glass-action-back-btn">〈 Back</button>
                <button type="button" onClick={() => { if (validateStep()) setCurrentStep(4); }} className="glass-action-continue-btn glass-adaptive-width">Continue 〉</button>
              </div>
            </div>
          )}

          {currentStep === 4 && (
            <div className="glass-step-workspace glass-fade-animation">
              <h2>Profile Photo</h2>
              <p className="glass-step-subtitle">Upload a professional photo for your expert profile</p>

              <div className="glass-avatar-upload-axis-wrapper">
                <div className="glass-avatar-circle-placeholder-ui">
                  {profilePhoto
                    ? <img src={URL.createObjectURL(profilePhoto)} alt="Preview" style={{ width: "100%", height: "100%", borderRadius: "50%", objectFit: "cover" }} />
                    : <span className="avatar-icon-svg">👤</span>
                  }
                </div>

                <input
                  ref={photoInputRef}
                  type="file"
                  accept="image/*"
                  style={{ display: "none" }}
                  onChange={e => {
                    const file = e.target.files[0];
                    if (file) setProfilePhoto(file);
                  }}
                />
                <button
                  type="button"
                  className="glass-native-trigger-upload-btn"
                  onClick={() => photoInputRef.current && photoInputRef.current.click()}
                >
                  📤 {profilePhoto ? "Change Photo" : "Upload Photo"}
                </button>

                {profilePhoto && (
                  <div className="glass-selected-file-pill-alert">✓ Photo Loaded: {profilePhoto.name}</div>
                )}
              </div>

              <div className="glass-tips-alert-box-framework">
                <p>💡 <strong>Tips:</strong> Use a professional headshot with a clean background. Clear face, good lighting, business attire preferred.</p>
              </div>

              <div className="glass-dual-navigation-buttons-row">
                <button type="button" onClick={() => setCurrentStep(3)} className="glass-action-back-btn">〈 Back</button>
                <button type="button" onClick={() => { if (validateStep()) setCurrentStep(5); }} className="glass-action-continue-btn glass-adaptive-width">Continue 〉</button>
              </div>
            </div>
          )}

          {currentStep === 5 && (
            <div className="glass-step-workspace glass-fade-animation">
              <h2>Legal Agreements</h2>
              <p className="glass-step-subtitle">Please review and accept the agreements to proceed</p>

              <div 
                className="glass-legal-document-row-card"
                onClick={() => setModalContent({ isOpen: true, title: "Privacy Policy", text: privacyPolicyText })}
                style={{ cursor: "pointer" }}
              >
                <span className="doc-icon-badge">🛡️</span>
                <div className="doc-meta-info">
                  <h4>Privacy Policy</h4>
                  <p>How we collect, store and protect your data</p>
                </div>
                <span className="doc-view-link">View →</span>
              </div>

              <div 
                className="glass-legal-document-row-card glass-margin-top-gap"
                onClick={() => setModalContent({ isOpen: true, title: "Terms of Service", text: termsOfServiceText })}
                style={{ cursor: "pointer" }}
              >
                <span className="doc-icon-badge">📄</span>
                <div className="doc-meta-info">
                  <h4>Terms of Service</h4>
                  <p>Platform rules and code of conduct for experts</p>
                </div>
                <span className="doc-view-link">View →</span>
              </div>

              <div className="glass-legal-checkbox-constraints-card">
                <label className="glass-checkbox-label">
                  <input type="checkbox" checked={agreedToTerms} onChange={e => setAgreedToTerms(e.target.checked)} className="glass-native-checkbox" />
                  <span className="glass-checkbox-custom-text">
                    I have read and agree to the Privacy Policy and Terms of Service. I confirm that I am a legitimate business expert.
                  </span>
                </label>
              </div>

              {/* Frosted Dark Glass Backdrop Overlay Modal Viewport Component */}
              {modalContent.isOpen && (
                <div className="reschedule-modal-overlay" onClick={() => setModalContent({ isOpen: false, title: "", text: "" })}>
                  <div className="reschedule-modal-card glass-legal-modal" onClick={(e) => e.stopPropagation()}>
                    
                    <h3 className="glass-legal-modal-title">{modalContent.title}</h3>
                    <p className="glass-legal-modal-subtitle">Please read through carefully</p>
                    
                    <div className="glass-legal-content-body">
                      {modalContent.text.split("\n\n").map((paragraph, index) => {
                        if (/^\d+\.\s/.test(paragraph.trim())) {
                          return (
                            <div key={index} className="glass-fade-animation">
                              <h4 className="legal-section-header">{paragraph.split("\n")[0]}</h4>
                              {paragraph.split("\n").slice(1).length > 0 && (
                                <p className="legal-paragraph-block">{paragraph.split("\n").slice(1).join("\n")}</p>
                              )}
                            </div>
                          );
                        }
                        return (
                          <p key={index} className="legal-paragraph-block glass-fade-animation">
                            {paragraph}
                          </p>
                        );
                      })}
                    </div>
                    
                    <div className="reschedule-modal-actions" style={{ padding: 0 }}>
                      <button className="glass-legal-close-btn" onClick={() => setModalContent({ isOpen: false, title: "", text: "" })}>
                        Close & Return
                      </button>
                    </div>
                    
                  </div>
                </div>
              )}

              <div className="glass-dual-navigation-buttons-row">
                <button type="button" onClick={() => setCurrentStep(4)} className="glass-action-back-btn" disabled={isSubmitting}>〈 Back</button>
                <button 
                  type="button" 
                  onClick={async (e) => {
                    e.preventDefault();

                    // Guard against multi-traffic: block re-entry if a submission is already in flight
                    if (isSubmitting) return;

                    setErrorBanner("");
                    if (!agreedToTerms) {
                      setErrorBanner("⚠️ You must review and accept the agreements to proceed.");
                      return;
                    }
                    
                    if (typeof setIsRegistering === "function") {
                      setIsRegistering(true);
                    }
                    
                    setIsSubmitting(true);
                    let createdUser = null;
                    let businessLogoUrl = "";
                    let profilePhotoUrl = "";
                    try {
                      setUploadStatus("Creating account...");
                      const userCredential = await createUserWithEmailAndPassword(auth, email.trim(), password);
                      createdUser = userCredential.user;
                      const idToken = await createdUser.getIdToken();

                      if (businessLogo) {
                        setUploadStatus("Saving business logo...");
                        businessLogoUrl = await uploadViaPresignedUrl(businessLogo, "experts/branding", idToken);
                      }
                      if (profilePhoto) {
                        setUploadStatus("Saving profile picture...");
                        profilePhotoUrl = await uploadViaPresignedUrl(profilePhoto, "experts/avatars", idToken);
                      }

                      setUploadStatus("Saving your profile to database...");

                      await setDoc(doc(db, "experts", createdUser.uid), {
                        uid: createdUser.uid,
                        firstName,
                        lastName,
                        displayName: `${firstName} ${lastName}`.trim(),
                        email,
                        company: companyName,
                        industry: industries.join(", "),
                        industries,
                        experienceYears: experienceYearRow,
                        bio,
                        location,
                        approvalStatus: "pending",
                        bankDetailsAdded: false,
                        bankEditStatus: "locked",
                        profilePhoto: profilePhotoUrl,
                        profilePic: profilePhotoUrl,
                        documentsUrls: { businessLogo: businessLogoUrl, profilePhoto: profilePhotoUrl },
                        isVisible:    false,
                        ratingSum:    0,
                        reviewsCount: 0,
                        avgRating:    0,
                        // ── Tracking fields ──────────────────────────────
                        signupDate:       serverTimestamp(),
                        sessionCount:     0,
                        lastLoginAt:      serverTimestamp(),
                        lastLogoutAt:     null,
                        deletionCount:    0,
                        profileUpdatedAt: serverTimestamp(),
                        stats: {
                          totalEarnings: 0,
                          monthEarnings: 0,
                          totalSessions: 0,
                          thisMonthSessions: 0,
                          pendingPayout: 0,
                        },
                        createdAt: serverTimestamp()
                      });

                      // Notify admin that a new expert profile needs review.
                      // Non-fatal — signup already succeeded above.
                      try {
                        await addDoc(collection(db, "adminRequests"), {
                          type:        "profile_approval",
                          expertId:    createdUser.uid,
                          expertName:  `${firstName} ${lastName}`.trim(),
                          expertEmail: email,
                          company:     companyName,
                          industries,
                          bio,
                          profilePhoto: profilePhotoUrl,
                          status:      "pending",
                          createdAt:   serverTimestamp(),
                        });
                      } catch (reqErr) {
                        console.error("Failed to create admin request:", reqErr);
                      }

                      // ── Reclaim deletion history from prior deleted expert accounts ──
                      // Prevents infinite re-signup by carrying forward deletionCount
                      // and previousUids from any deleted expert docs with this email.
                      try {
                        const deletedExpertQuery = await getDocs(
                          query(collection(db, "experts"),
                            where("email", "==", email.trim().toLowerCase()),
                            where("approvalStatus", "==", "deleted")
                          )
                        );
                        const staleDocs = deletedExpertQuery.docs.filter(d => d.id !== createdUser.uid);
                        if (staleDocs.length > 0) {
                          let totalDeletions = 0;
                          const previousUids = [];
                          for (const d of staleDocs) {
                            totalDeletions += d.data().deletionCount || 1;
                            previousUids.push(d.id);
                          }
                          await setDoc(doc(db, "experts", createdUser.uid), {
                            priorDeletionCount: totalDeletions,
                            previousUids,
                          }, { merge: true });
                        }
                      } catch (reclaimErr) {
                        console.warn("Expert reclaim failed (non-fatal):", reclaimErr.message);
                      }

                      // Set portal:"expert" custom claim so future logins
                      // to the user portal are blocked instantly — no
                      // Firestore query, zero authenticated window.
                      try {
                        await fetch(`${API_BASE_URL}/api/experts/set-portal-claim`, {
                          method: "POST",
                          headers: { "Content-Type": "application/json", Authorization: `Bearer ${idToken}` },
                        });
                      } catch (claimErr) {
                        // Non-fatal — Firestore fallback covers legacy accounts.
                        console.warn("set-portal-claim failed (non-fatal):", claimErr.message);
                      }

                      setIsSubmitting(false);
                      setUploadStatus("");
                      setCurrentStep(6);
                    } catch (err) {
                      console.error("Submission error caught:", err.code, err.message, err);
                      let friendlyMessage = "❌ Something went wrong during submission. Please try again.";
                      if (err.code === "auth/email-already-in-use") {
                        friendlyMessage = "❌ An account with this email already exists. Please sign in instead or use a different email address.";
                      } else if (err.code === "auth/invalid-email") {
                        friendlyMessage = "⚠️ The email address entered is not valid.";
                      } else if (err.code === "auth/weak-password") {
                        friendlyMessage = "⚠️ Password is too weak. Please choose a stronger password (min 8 characters).";
                      } else if (err.code === "auth/network-request-failed") {
                        friendlyMessage = "📡 Network error. Please check your connection and try again.";
                      } else if (err.code === "auth/too-many-requests") {
                        friendlyMessage = "🔒 Too many attempts. Please wait a moment and try again.";
                      } else if (err.message && err.message.includes("S3 upload failed")) {
                        friendlyMessage = "📁 We couldn't upload your file. Please try a different image or try again.";
                      }
                      // Note: unknown/unmapped errors always fall back to the generic message above,
                      // never showing raw err.message to the user. Full details are logged to console.
                      setErrorBanner(friendlyMessage);
                      setUploadStatus("");
                      
                      if (typeof setIsRegistering === "function") {
                        setIsRegistering(false);
                      }

                      // Note: any partially-uploaded S3 objects (businessLogoUrl /
                      // profilePhotoUrl) can no longer be cleaned up directly from the
                      // browser now that it has no S3 credentials — that's the point of
                      // this fix. Orphaned files under experts/branding|avatars/<uid>/
                      // are harmless (the uid's Firestore doc and Auth account are
                      // removed below) but a periodic backend cleanup job or S3 lifecycle
                      // rule can be added if this needs tidying up automatically.
                      if (createdUser) {
                        try { await deleteUser(createdUser); } catch (_) {}
                      }
                      setIsSubmitting(false);
                    }
                  }} 
                  className="glass-action-continue-btn glass-adaptive-width glass-theme-submit-color" 
                  disabled={isSubmitting}
                >
                  {isSubmitting ? uploadStatus || "Processing..." : "Submit Application 〉"}
                </button>
              </div>
            </div>
          )}

          {currentStep === 6 && (
            <div className="glass-step-workspace glass-fade-animation glass-center-axis-layout">
              <div className="glass-success-animated-badge">✓</div>
              <h2>Application Submitted!</h2>
              <p className="glass-success-description">
                Welcome to SORTED, {firstName}! Your profile is under review. You'll receive an email within 24–48 hours once verified.
              </p>

              <button 
                type="button" 
                onClick={async () => {
                  if (typeof setIsRegistering === "function") {
                    setIsRegistering(false);
                  }
                  try {
                    await auth.signOut();
                  } catch (_) {}
                  if (typeof onLoginClick === 'function') {
                    onLoginClick();
                  }
                }} 
                className="glass-success-master-dashboard-redirect-btn"
              >
                Go to Expert Login →
              </button>
            </div>
          )}

        </div>
      </div>
    </div>
  );
}

export default ExpertSignup;